import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedIcon from "@mui/icons-material/Verified";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Paper,
  Chip,
  Avatar,
  Button,
  alpha,
  LinearProgress,
  Stack,
  MenuItem,
  Select,
  FormControl,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { adminDataGridBoxSx, adminPageSx } from "../../theme/adminLayout";
import { API_BASE_URL } from "../../config/apiBase";

const FormsUser = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,

    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },

    primary: {
      100: "#ffffff",
      200: "#fafafa",
      300: "#f5f5f5",
      400: "#ffffff",
      500: "#ffffff",
      600: "#e6e6e6",
      700: "#cccccc",
      800: "#b3b3b3",
      900: "#999999",
    },
  };

  const token = localStorage.getItem("accessToken");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    unverified: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/seller/getSellers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }
      const responseData = await response.json();

      if (!responseData.success || !Array.isArray(responseData.seller)) {
        throw new Error("Invalid response data format");
      }

      const formatDate = (isoDateString) => {
        const date = new Date(isoDateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      };

      const mappedData = responseData.seller.map((item) => ({
        id: item.id,
        email: item.email,
        firstName: item.firstName,
        lastName: item.lastName,
        contactNumber: item.contactNumber,
        createdAt: formatDate(item.createdAt),
        isVerified: item.isVerified,
        role: item.role,
        // Create full name for avatar
        fullName: `${item.firstName} ${item.lastName}`,
      }));

      const sortedData = mappedData.sort((a, b) => b.id - a.id);
      setData(sortedData);

      // Calculate statistics
      const totalSellers = sortedData.length;
      const verifiedSellers = sortedData.filter(
        (seller) => seller.isVerified
      ).length;

      setStats({
        total: totalSellers,
        verified: verifiedSellers,
        unverified: totalSellers - verifiedSellers,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error!", "Failed to fetch seller data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationChange = async (id, newStatus) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/seller/verifiedSeller/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            isVerified: newStatus.toString(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update verification status: ${response.statusText}`
        );
      }

      const responseData = await response.json();

      // Update local data
      const updatedData = data.map((item) => {
        if (item.id === id) {
          return { ...item, isVerified: newStatus };
        }
        return item;
      });

      setData(updatedData);

      // Update stats
      const prevVerified = data.find((item) => item.id === id).isVerified;
      setStats((prev) => ({
        ...prev,
        verified: prevVerified
          ? newStatus
            ? prev.verified
            : prev.verified - 1
          : newStatus
          ? prev.verified + 1
          : prev.verified,
        unverified: prevVerified
          ? newStatus
            ? prev.unverified
            : prev.unverified + 1
          : newStatus
          ? prev.unverified - 1
          : prev.unverified,
      }));

      Swal.fire({
        title: "Success!",
        text:
          responseData.message ||
          `Seller ${newStatus ? "verified" : "unverified"} successfully`,
        icon: "success",
        background: "#ffffff",
        color: "#333333",
        timer: 2000,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Error updating verification status:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to update verification status.",
        icon: "error",
        background: "#ffffff",
        color: "#333333",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this seller!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.redAccent[500],
      cancelButtonColor: colors.grey[500],
      confirmButtonText: "Delete",
      background: "#ffffff",
      color: "#333333",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/seller/deleteSeller/${id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to delete seller");
          }

          const updatedData = data.filter((item) => item.id !== id);
          setData(updatedData);

          // Update stats after deletion
          const deletedSeller = data.find((item) => item.id === id);
          setStats((prev) => ({
            total: prev.total - 1,
            verified: deletedSeller.isVerified
              ? prev.verified - 1
              : prev.verified,
            unverified: !deletedSeller.isVerified
              ? prev.unverified - 1
              : prev.unverified,
          }));

          Swal.fire({
            title: "Deleted!",
            text: "The seller has been deleted.",
            icon: "success",
            background: "#ffffff",
            color: "#333333",
          });
        } catch (error) {
          console.error("Error deleting seller:", error);
          Swal.fire("Error!", "Failed to delete seller.", "error");
        }
      }
    });
  };

  const handleViewClick = (id) => {
    navigate(`/seller/${id}`);
  };

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  // Get avatar color based on name - using blue shades only
  const getAvatarColor = (name) => {
    const blueShades = [
      "#22a3f1", // Main blue
      "#4db5f5", // Lighter blue
      "#1b82c1", // Darker blue
      "#67baf0", // Even lighter blue
      "#146291", // Even darker blue
    ];

    const charCodeSum = name
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return blueShades[charCodeSum % blueShades.length];
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "fullName",
      headerName: "Seller",
      flex: 1.8,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              bgcolor: getAvatarColor(params.row.fullName),
              width: 32,
              height: 32,
              mr: 1,
            }}
          >
            {getInitials(params.row.fullName)}
          </Avatar>
          <Box>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize="14px"
              color="#333333"
            >
              {`${params.row.firstName} ${params.row.lastName}`}
            </Typography>
            
          </Box>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      renderCell: (params) => (
        <Typography color="#333333">{params.row.email}</Typography>
      ),
    },
    {
      field: "contactNumber",
      headerName: "Contact",
      flex: 0.9,
      renderCell: (params) => (
        <Typography color="#333333">{params.row.contactNumber}</Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="#333333">
          {params.row.createdAt}
        </Typography>
      ),
    },
    {
      field: "isVerified",
      headerName: "Status",
      flex: 0.9,
      renderCell: (params) => (
        <FormControl
          size="small"
          sx={{
            minWidth: 100,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              height: "28px",
              backgroundColor: params.row.isVerified
                ? alpha("#4CAF50", 0.1)
                : alpha("#757575", 0.1),
              border: `1px solid ${
                params.row.isVerified
                  ? alpha("#4CAF50", 0.3)
                  : alpha("#757575", 0.3)
              }`,
              "& fieldset": { border: "none" },
            },
          }}
        >
          <Select
            value={params.row.isVerified}
            onChange={(e) =>
              handleVerificationChange(params.row.id, e.target.value)
            }
            displayEmpty
            inputProps={{ "aria-label": "Verification Status" }}
            sx={{
              color: params.row.isVerified ? "#2E7D32" : "#616161",
              fontSize: "11px",
              fontWeight: "bold",
              ".MuiSelect-select": {
                padding: "2px 6px",
                display: "flex",
                alignItems: "center",
              },
            }}
            startAdornment={
              params.row.isVerified ? (
                <VerifiedIcon
                  sx={{ fontSize: 12, mr: 0.5, color: "#2E7D32" }}
                />
              ) : (
                <PersonIcon sx={{ fontSize: 12, mr: 0.5, color: "#616161" }} />
              )
            }
          >
            <MenuItem value={true} sx={{ fontSize: "11px" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    color: "#2E7D32",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  Verified
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value={false} sx={{ fontSize: "11px" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    color: "#616161",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  Unverified
                </Typography>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.row.role}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: alpha(colors.blueAccent[500], 0.2),
            color: colors.blueAccent[500],
          }}
        />
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleViewClick(params.row.id)}
              sx={{
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
                borderRadius: "4px",
                mr: 1,
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(colors.blueAccent[500], 0.2),
                },
              }}
            >
              <VisibilityIcon
                sx={{ color: colors.blueAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Seller">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id)}
              sx={{
                backgroundColor: alpha(colors.redAccent[500], 0.1),
                borderRadius: "4px",
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(colors.redAccent[500], 0.2),
                },
              }}
            >
              <DeleteIcon
                sx={{ color: colors.redAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Custom toolbar component
  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 16px",
          borderBottom: `1px solid #e0e0e0`,
        }}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            sx={{
              borderColor: alpha(colors.blueAccent[500], 0.5),
              color: colors.blueAccent[500],
              mr: 2,
              "&:hover": {
                borderColor: colors.blueAccent[500],
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
              },
            }}
          >
            Filter
          </Button>
          <GridToolbar
            sx={{
              "& .MuiButton-root": {
                color: "#757575",
                "&:hover": {
                  backgroundColor: alpha("#e0e0e0", 0.5),
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={adminPageSx}>
      {/* Header with inline stats */}
      <Box
        mb={2}
        p={{ xs: 2, md: 2.5 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          color="white"
          sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
        >
          Seller Management
        </Typography>

        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body1" color="white">
            Manage and monitor all registered sellers in the system
          </Typography>

          {/* Inline stats */}
          <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Total: ${stats.total}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
              }}
            />
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
              label={`Verified: ${stats.verified}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(76, 175, 80, 0.3)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
            <Chip
              icon={<PersonIcon sx={{ fontSize: 14 }} />}
              label={`Unverified: ${stats.unverified}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}
      >
        <Box
          sx={{
            ...adminDataGridBoxSx,
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              fontSize: "13px",
              color: "#333333",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid #e0e0e0`,
              padding: "12px",
              fontSize: "13px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#22a3f1",
              borderBottom: "none",
              fontSize: "14px",
              fontWeight: "bold",
              padding: "0 12px",
              color: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: "rgba(34, 163, 241, 0.05)",
              padding: "6px 12px",
              color: "#333333",
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "rgba(34, 163, 241, 0.1)",
              },
            },
            "& .MuiTablePagination-root": {
              color: "#333333",
            },
            "& .MuiIconButton-root": {
              color: "#22a3f1",
            },
            "& .MuiCheckbox-root": {
              color: "#22a3f1",
            },
            "& .MuiSvgIcon-root": {
              "&.MuiSelect-icon": {
                color: "#757575",
              },
            },
            "& .MuiMenuItem-root": {
              color: "#333333",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            columnVisibilityModel={{
              id: !isMobile,
              email: !isMobile,
              contactNumber: !isMobile,
              createdAt: !isMobile,
              role: !isMobile,
            }}
            components={{
              Toolbar: CustomToolbar,
              LoadingOverlay: LinearProgress,
            }}
            loading={loading}
            disableSelectionOnClick
            rowHeight={56}
            pageSize={8}
            rowsPerPageOptions={[8, 16, 32]}
            sx={{
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default FormsUser;
